import React, { useCallback, useId, useState } from 'react';

import { IconButton, ListItemText, Menu, MenuItem } from '@mui/material';
import { RichTreeViewPro } from '@mui/x-tree-view-pro/RichTreeViewPro';
import { FolderPlus } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { SkillAttachmentEditor } from './skill-attachment-editor';
import type { SkillAttachmentChanges } from './skill-attachment-editor';
import { DROP_TARGET_COLOR, SkillTreeDragArea } from './skill-tree-drag';
import {
  SKILL_TREE_SLOTS,
  SkillTreeActionsContext,
  SkillTreeDraftContext,
  SkillTreeRenameContext,
} from './skill-tree-item';
import type { SkillFileTree } from './use-skill-file-tree';

/** The folders the Agent Skills spec suggests, offered first when adding one. */
const SUGGESTED_FOLDERS = [
  { description: 'Documents Aline reads when needed', name: 'references' },
  { description: 'Templates and other files to reuse', name: 'assets' },
];
const FOLDER_MENU_SLOT_PROPS = {
  paper: {
    sx: {
      '& .MuiList-root': { py: 0.5 },
      '& .MuiListItemText-primary': { fontSize: 14, lineHeight: 1.4 },
      '& .MuiListItemText-root': { my: 0 },
      '& .MuiListItemText-secondary': { fontSize: 12, lineHeight: 1.4, whiteSpace: 'normal' },
      '& .MuiMenuItem-root': { minHeight: 0, px: 1.5, py: 0.75 },
      maxWidth: 'calc(100vw - 32px)',
      width: 280,
    },
  },
};

const useStyles = makeStyles()(theme => ({
  actions: { alignItems: 'center', display: 'flex' },
  label: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: 12,
    fontWeight: 600,
    justifyContent: 'space-between',
    letterSpacing: '0.06em',
    minHeight: 56,
    padding: '0 16px',
    [theme.breakpoints.down('md')]: { display: 'none' },
  },
  newFolderButton: {
    '&[aria-expanded="true"]': {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.primary.main,
    },
    height: 28,
    width: 28,
  },
  root: {
    backgroundColor: '#FAFBFC',
    borderRight: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: 8,
    padding: 0,
    width: 260,
    [theme.breakpoints.down('md')]: {
      borderBottom: `1px solid ${theme.appColors.rule}`,
      borderRight: 0,
      flexDirection: 'row',
      padding: 12,
      width: '100%',
    },
  },
  tree: {
    '& .MuiTreeItem-content': {
      borderRadius: 6,
      marginBottom: 2,
      padding: '7px 8px',
      paddingLeft: 'calc(8px + var(--TreeView-itemDepth) * 24px)',
    },
    // Row actions take room only when shown, so names are not cut short for them.
    '& .MuiTreeItem-content:hover .skill-tree-actions, & .MuiTreeItem-content:focus-within .skill-tree-actions, & .MuiTreeItem-content:has([aria-expanded="true"]) .skill-tree-actions':
      { opacity: 1, width: 'auto' },
    // The tree view marks state with data attributes; the focused and hover variants outrank its own.
    '& .MuiTreeItem-content[data-selected], & .MuiTreeItem-content[data-selected][data-focused], & .MuiTreeItem-content[data-selected]:hover':
      {
        backgroundColor: '#EDF4FF',
        color: '#182537',
        fontWeight: 600,
      },
    '& .MuiTreeItem-label': {
      fontSize: 14,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .skill-tree-actions': { opacity: 0, overflow: 'hidden', width: 0 },
    '& .skill-tree-dragging': { opacity: 0.45 },
    '& .skill-tree-drop-target > .MuiTreeItem-content': {
      backgroundColor: 'rgba(22, 119, 255, 0.08)',
      boxShadow: `inset 0 0 0 1.5px ${DROP_TARGET_COLOR}`,
    },
    flex: '0 0 auto',
    height: 'auto',
    minHeight: 0,
    padding: '0 6px',
  },
}));

const SuggestedFolderMenuItem = React.memo(function SuggestedFolderMenuItem({
  description,
  name,
  onSelect,
}: {
  description: string;
  name: string;
  onSelect: (name: string) => void;
}) {
  const select = useCallback(() => onSelect(name), [name, onSelect]);
  return (
    <MenuItem onClick={select}>
      <ListItemText primary={`${name}/`} secondary={description} />
    </MenuItem>
  );
});

/** New folder: one of the suggested folders, or a custom one named in the tree. */
const NewFolderMenu = React.memo(function NewFolderMenu({
  onAddSuggestedFolder,
  onNewFolder,
}: {
  onAddSuggestedFolder: (name: string) => void;
  onNewFolder: () => void;
}) {
  const { classes } = useStyles();
  const menuId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = useCallback(
    (event: React.MouseEvent<HTMLElement>) => setAnchor(event.currentTarget),
    []
  );
  const close = useCallback(() => setAnchor(null), []);
  const addSuggested = useCallback(
    (name: string) => {
      setAnchor(null);
      onAddSuggestedFolder(name);
    },
    [onAddSuggestedFolder]
  );
  const addCustom = useCallback(() => {
    setAnchor(null);
    onNewFolder();
  }, [onNewFolder]);
  return (
    <>
      <AlineTooltip arrow placement="top" title="New folder">
        <IconButton
          aria-controls={anchor ? menuId : undefined}
          aria-expanded={Boolean(anchor)}
          aria-haspopup="menu"
          aria-label="New folder"
          className={classes.newFolderButton}
          size="small"
          onClick={open}
        >
          <FolderPlus size={16} />
        </IconButton>
      </AlineTooltip>
      <Menu
        anchorEl={anchor}
        id={menuId}
        open={Boolean(anchor)}
        slotProps={FOLDER_MENU_SLOT_PROPS}
        onClose={close}
      >
        {SUGGESTED_FOLDERS.map(({ description, name }) => (
          <SuggestedFolderMenuItem
            key={name}
            description={description}
            name={name}
            onSelect={addSuggested}
          />
        ))}
        <MenuItem onClick={addCustom}>
          <ListItemText primary="Custom folder..." />
        </MenuItem>
      </Menu>
    </>
  );
});

/** The skill's files: the tree, and for the author the controls that add files and folders. */
export const SkillFileNavigation = React.memo(function SkillFileNavigation({
  attachmentChanges,
  canEdit,
  onAttachmentChange,
  skill,
  tree,
}: {
  attachmentChanges: SkillAttachmentChanges;
  canEdit: boolean;
  onAttachmentChange: React.Dispatch<React.SetStateAction<SkillAttachmentChanges>>;
  skill: Apollo.AgentSkillSimpleFragment;
  tree: SkillFileTree;
}) {
  const { classes } = useStyles();
  return (
    <nav aria-label="Skill editor" className={classes.root}>
      <div className={classes.label}>
        <span>FILES</span>
        <div className={classes.actions}>
          {canEdit && (
            <>
              <SkillAttachmentEditor
                actionsOnly
                changes={attachmentChanges}
                editable={canEdit}
                folderPath={tree.creationFolder}
                folders={tree.folders}
                skill={skill}
                onChange={onAttachmentChange}
                onNewFile={tree.openNewFile}
              />
              <NewFolderMenu
                onAddSuggestedFolder={tree.addSuggestedFolder}
                onNewFolder={tree.openNewFolder}
              />
            </>
          )}
        </div>
      </div>
      <SkillTreeActionsContext.Provider value={tree.treeActions}>
        <SkillTreeRenameContext.Provider value={tree.renameInput}>
          <SkillTreeDraftContext.Provider value={tree.draftInput}>
            <SkillTreeDragArea {...tree.dragProps}>
              <RichTreeViewPro
                apiRef={tree.treeApiRef}
                aria-label="Skill contents"
                className={classes.tree}
                expandedItems={tree.expandedFolders}
                itemChildrenIndentation={24}
                items={tree.treeItems}
                selectedItems={tree.selectedItemId}
                slots={SKILL_TREE_SLOTS}
                onExpandedItemsChange={tree.changeExpandedFolders}
                onItemFocus={tree.handleTreeItemFocus}
                onSelectedItemsChange={tree.handleTreeSelection}
              />
            </SkillTreeDragArea>
          </SkillTreeDraftContext.Provider>
        </SkillTreeRenameContext.Provider>
      </SkillTreeActionsContext.Provider>
    </nav>
  );
});
